import {getErrorString} from './error';
import {type IWindowFetchMessage, type IWindowMessage} from '../../@type/window-messages';
import {captureSentryException} from './sentry';

/**
 * Resulting helper function to comply with typescript by
 * always checking the result of $qs
 *
 * @param { string } 		selector html query selector string
 * @param { (T) => void } 	cb callback function that will only be called if the element is found
 */
function $qs<T extends HTMLElement>(selector: string, cb: null | (($element: T) => void) = null): T | null {
	const $element = document.querySelector<T>(selector);
	if ($element && cb !== null) {
		cb($element);
	}

	return $element;
}

/**
 * Helper function for "document.querySelectorAll" that always returns a proper array
 * and has the base type "HTMLElement" instead of the almost never used "Element".
 */
function $qsAll<T extends HTMLElement>(selector: string, callback?: ($element: T) => void): T[] {
	const result = Array.from(document.querySelectorAll<T>(selector));

	if (callback) {
		for (const $element of result) {
			callback($element);
		}
	}

	return result;
}

/**
 * Loads a given JS script.
 *
 * @param src URL of the script
 * @param scriptWindowObject Name of the object the script defines if the script defines any objects
 */
async function loadScript(src: string, scriptWindowObject: string | null, callback?: () => void): Promise<void> {
	return new Promise((resolve, reject) => {
		if (document.querySelector(`script[src="${src}"]`) ?? (window as any)[scriptWindowObject ?? '']) {
			callback?.();
			resolve();
		}

		const $script = document.createElement('script');
		$script.type = 'text/javascript';
		$script.src = src;

		($script as any).onreadystatechange = () => {
			callback?.();
			resolve();
		};

		$script.onload = () => {
			callback?.();
			resolve();
		};

		$script.onerror = reject;

		document.head.appendChild($script);
	});
}

interface WindowFetchData<T> {
	error: Error;
	result: T;
}

async function fetchWindowData<TRequest, TResponse>(targetWindow: Window | null, endpoint: string, request?: TRequest): Promise<TResponse> {
	return new Promise((resolve, reject) => {
		const channel = new MessageChannel();
		channel.port1.onmessage = ({data}) => {
			channel.port1.close();

			if ((data as WindowFetchData<TResponse>).error) {
				reject((data as WindowFetchData<TResponse>).error);
			} else {
				resolve((data as WindowFetchData<TResponse>).result);
			}
		};

		if (targetWindow) {
			targetWindow.postMessage(
				{
					event: endpoint,
					request,
				},
				location.origin,
				[channel.port2],
			);
		} else {
			reject(new Error('Target window is not valid.'));
		}
	});
}

/**
 * Allows for async request from the host page for data.
 */
function onWindowDataFetch<TRequest, TResponse>(endpoint: string, requestCallback: (request: TRequest) => Promise<TResponse> | TResponse): void {
	window.addEventListener('message', async (message: MessageEvent<IWindowFetchMessage<TRequest>>) => {
		if (message.origin !== location.origin) {
			return;
		}

		if (message.data.event === endpoint) {
			try {
				const response = await requestCallback(message.data.request as unknown as TRequest);

				message.ports?.[0]?.postMessage({result: response});
			} catch (error: unknown) {
				try {
					message.ports?.[0]?.postMessage({error});
				} catch (secondaryError: unknown) {
					message.ports?.[0]?.postMessage({error: new Error(getErrorString(error))});
					captureSentryException(secondaryError as Error, {
						originalError: error,
						originalErrorString: getErrorString(error),
					});
				}
			}
		}
	});
}

async function fetchHostWindowData<TRequest, TResponse>(endpoint: string, request?: TRequest): Promise<TResponse> {
	return fetchWindowData(window.top, endpoint, request);
}

/**
 * Helper function to give strong typing to a Message Event
 */
function onWindowMessage<T extends IWindowMessage>(eventName: string, cb: (event: T) => void | Promise<void>) {
	window.addEventListener('message', async (event: MessageEvent<T>) => {
		if (event.origin !== location.origin) {
			return;
		}

		if (event.data.event === eventName) {
			await cb(event.data);
		}
	}, false);
}

// https://ourcodeworld.com/articles/read/376/how-to-strip-html-from-a-string-extract-only-text-content-in-javascript
function stripHtml(html: string, preFilterSelector: string | null = 'a'): string {
	const temporalDivElement = document.createElement('div');
	temporalDivElement.innerHTML = html;

	if (preFilterSelector) {
		temporalDivElement.querySelectorAll(preFilterSelector).forEach($el => {
			$el.remove();
		});
	}

	// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
	return (temporalDivElement.textContent || temporalDivElement.innerText || '').trim();
}

function isMobile() {
	return window.innerWidth < 900;
}

export {
	$qs,
	$qsAll,
	stripHtml,
	loadScript,
	fetchWindowData,
	fetchHostWindowData,
	onWindowMessage,
	onWindowDataFetch,
	isMobile,
};
